/**
 * Which chrome a screen gets (spec 5): tab screens have the header and the
 * bottom menu, step screens the step bar and Telegram's back button, the rest
 * a plain layout. Pure functions of the path, so they are tested without a DOM.
 */
export type Layout = 'tab' | 'step' | 'plain';
export type TabId = 'invite' | 'library' | 'create' | 'credits' | 'profile';
export type StepId = 'idea' | 'text' | 'voice';
export type StepState = 'done' | 'current' | 'todo';

/** The bottom menu, in order; «Создать» is the centre. */
export const TABS: ReadonlyArray<{ id: TabId; path: string }> = [
  { id: 'invite', path: '/invite' },
  { id: 'library', path: '/library' },
  { id: 'create', path: '/' },
  { id: 'credits', path: '/credits' },
  { id: 'profile', path: '/settings' },
];

const STEPS: ReadonlyArray<StepId> = ['idea', 'text', 'voice'];

/** `/library/?tab=ready` → `/library`: no query, hash or trailing slash; the root stays `/`. */
function normalize(pathname: string): string {
  const trimmed = pathname.replace(/[?#].*$/, '').replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function activeTab(pathname: string): TabId | null {
  const path = normalize(pathname);
  return TABS.find((tab) => tab.path === path)?.id ?? null;
}

export function stepFor(pathname: string): StepId | null {
  const path = normalize(pathname);
  if (/^\/thoughts\/\d+$/.test(path)) return 'idea';
  if (/^\/drafts\/\d+$/.test(path)) return 'text';
  if (/^\/drafts\/\d+\/(voice|own|style)$/.test(path)) return 'voice';
  if (/^\/renders\/\d+$/.test(path)) return 'voice';
  return null;
}

export function layoutFor(pathname: string): Layout {
  if (activeTab(pathname)) return 'tab';
  if (stepFor(pathname)) return 'step';
  return 'plain';
}

/**
 * The dots of the step bar — Мысль → Идея → Формат и текст → Озвучка; «Мысль» is
 * always behind. A carousel has no «Озвучка»: it is built from «Формат и текст»,
 * so its result screen stands on that step.
 */
export function stepStates(
  current: StepId,
  opts: { carousel?: boolean } = {},
): ReadonlyArray<{ id: 'thought' | StepId; state: StepState }> {
  const steps = opts.carousel ? STEPS.filter((s) => s !== 'voice') : STEPS;
  const at = steps.indexOf(current === 'voice' && opts.carousel ? 'text' : current);
  return [
    { id: 'thought', state: 'done' },
    ...steps.map((id, i) => ({ id, state: (i < at ? 'done' : i === at ? 'current' : 'todo') as StepState })),
  ];
}

/**
 * Where Telegram's back button leads from a step: the explicit parent, never
 * history.back() — a screen opened from a bot notification has no history.
 * An unknown parent (data not loaded yet) leads home.
 */
export function stepBack(
  pathname: string,
  ids: { thoughtId?: number | null; draftId?: number | null },
): string {
  const path = normalize(pathname);
  if (/^\/drafts\/\d+$/.test(path)) return ids.thoughtId != null ? `/thoughts/${ids.thoughtId}` : '/';
  const draftChild = path.match(/^\/drafts\/(\d+)\/(voice|own|style)$/);
  if (draftChild) return draftChild[2] === 'own' ? `/drafts/${draftChild[1]}/voice?voice=own` : `/drafts/${draftChild[1]}`;
  if (/^\/renders\/\d+$/.test(path)) return ids.draftId != null ? `/drafts/${ids.draftId}` : '/';
  return '/';
}

/** An on-screen keyboard takes at least this much height; less is browser bars coming and going. */
const KEYBOARD_MIN_PX = 150;

/**
 * The keyboard is open when a text field has focus and the visible viewport has
 * actually shrunk under it. Focus alone is not enough: Android's Back closes the
 * keyboard but leaves the field focused, and a desktop has no keyboard to hide for.
 */
export function keyboardOpen(textFocused: boolean, viewportHeight: number, fullHeight: number): boolean {
  return textFocused && fullHeight - viewportHeight >= KEYBOARD_MIN_PX;
}

const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'number', 'password']);

/** Whether focusing this element opens the on-screen keyboard (the menu and the step button hide then). */
export function isTextEntry(el: { tagName: string; type?: string; isContentEditable?: boolean } | null): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((el.type ?? '').toLowerCase());
  return false;
}
